import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CaretDown, CaretUp, Plus, X } from '@phosphor-icons/react';

const Section = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-slate-50 transition-colors text-left"
      >
        <span className="text-sm font-medium text-slate-700">{title}</span>
        {open ? <CaretUp size={16} className="text-slate-400" /> : <CaretDown size={16} className="text-slate-400" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 space-y-3">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const RepeatableItem = ({ items, setItems, nameLabel, namePlaceholder, yearPlaceholder, nameField = 'name' }) => {
  const [draft, setDraft] = useState({ [nameField]: '', approximate_year: '' });

  const add = () => {
    if (!draft[nameField].trim()) return;
    setItems([...items, { ...draft, [nameField]: draft[nameField].trim() }]);
    setDraft({ [nameField]: '', approximate_year: '' });
  };

  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} className="bg-white border border-slate-200 rounded-lg px-3 py-2 flex items-center justify-between">
          <span className="text-sm text-slate-700">
            {item[nameField]}
            {item.approximate_year && <span className="text-slate-400 ml-1">({item.approximate_year})</span>}
          </span>
          <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-slate-300 hover:text-red-500">
            <X size={14} />
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <input
          placeholder={namePlaceholder}
          value={draft[nameField]}
          onChange={(e) => setDraft(d => ({ ...d, [nameField]: e.target.value }))}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none"
        />
        <input
          placeholder={yearPlaceholder || "Year (approx)"}
          value={draft.approximate_year}
          onChange={(e) => setDraft(d => ({ ...d, approximate_year: e.target.value }))}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          className="w-28 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none"
        />
        <button onClick={add} disabled={!draft[nameField].trim()} className="text-[#235347] disabled:text-slate-300">
          <Plus size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
};

const TagInput = ({ items, setItems, placeholder }) => {
  const [input, setInput] = useState('');

  const add = () => {
    const val = input.trim();
    if (!val || items.includes(val)) return;
    setItems([...items, val]);
    setInput('');
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, idx) => (
          <span key={idx} className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-3 py-1 text-sm text-slate-700">
            {item}
            <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-slate-300 hover:text-red-500">
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none"
        />
        <button onClick={add} disabled={!input.trim()} className="text-[#235347] disabled:text-slate-300">
          <Plus size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
};

export const OnboardingHistory = ({ data, onChange }) => {
  const history = data.medical_history || {
    nothing_significant: false,
    past_conditions: [],
    hospitalisations: [],
    surgeries: [],
    recurring_issues: [],
  };

  const update = (field, value) => {
    onChange({ ...data, medical_history: { ...history, [field]: value } });
  };

  if (history.nothing_significant) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
        <div className="bg-white border border-slate-150 rounded-2xl p-5">
          <p className="text-slate-700 leading-relaxed">Let's quickly cover your medical history.</p>
          <p className="text-sm text-slate-400 mt-2">I'll keep this brief — just the things that might still matter.</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
          <p className="text-sm text-emerald-800 font-medium">Marked as nothing significant</p>
          <button
            onClick={() => update('nothing_significant', false)}
            className="text-xs text-emerald-600 hover:underline mt-1"
          >
            Undo
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      {/* Kindra message */}
      <div className="bg-white border border-slate-150 rounded-2xl p-5">
        <p className="text-slate-700 leading-relaxed">Let's quickly cover your medical history.</p>
        <p className="text-sm text-slate-400 mt-2">I'll keep this brief — just the things that might still matter.</p>
      </div>

      <div className="space-y-3">
        <Section title="Past conditions or diagnoses">
          <RepeatableItem
            items={history.past_conditions}
            setItems={(v) => update('past_conditions', v)}
            namePlaceholder="Condition name"
          />
        </Section>

        <Section title="Hospitalisations">
          <RepeatableItem
            items={history.hospitalisations}
            setItems={(v) => update('hospitalisations', v)}
            nameField="reason"
            namePlaceholder="Reason for stay"
          />
        </Section>

        <Section title="Surgeries">
          <RepeatableItem
            items={history.surgeries}
            setItems={(v) => update('surgeries', v)}
            nameField="type"
            namePlaceholder="Type of surgery"
          />
        </Section>

        <Section title="Recurring issues">
          <TagInput
            items={history.recurring_issues}
            setItems={(v) => update('recurring_issues', v)}
            placeholder="e.g. back pain, migraines"
          />
        </Section>
      </div>

      <button
        data-testid="onboarding-history-nothing"
        onClick={() => update('nothing_significant', true)}
        className="w-full py-3 text-sm text-slate-500 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
      >
        Nothing significant
      </button>
    </motion.div>
  );
};
